import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { BRAND_NAME } from "@/lib/brand";

const HERO_BG = "https://images.pexels.com/photos/9367109/pexels-photo-9367109.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

export default function Login() {
    const { login, setUser } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("demo@lawfirm.com");
    const [password, setPassword] = useState("Demo@2026");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Welcome back");
            navigate("/app");
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const onGoogleSuccess = async (cred) => {
        try {
            const { data } = await api.post("/auth/google", { credential: cred.credential });
            localStorage.setItem("aic_token", data.token);
            setUser(data.user);
            toast.success("Signed in with Google");
            navigate("/app");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Google sign-in failed");
        }
    };

    const inner = (
        <div className="min-h-screen grid lg:grid-cols-2 bg-white">
            <div className="relative hidden lg:block">
                <img src={HERO_BG} alt="Law office" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-br from-[#0B2447]/95 via-[#0B2447]/80 to-[#0B2447]/60" />
                <div className="relative z-10 h-full flex flex-col p-12 text-white">
                    <Link to="/" className="font-display text-3xl font-extrabold tracking-tight">{BRAND_NAME}</Link>
                    <div className="mt-auto max-w-md">
                        <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-300 mb-4">
                            Practice Management · AI Suite
                        </div>
                        <h2 className="font-display text-4xl font-bold leading-tight">
                            Run your firm the way you run a trial — deliberately.
                        </h2>
                        <p className="mt-4 text-slate-200/90 leading-relaxed">
                            Cases, deadlines, documents, time and billing — in one workspace,
                            with Claude built in.
                        </p>
                    </div>
                </div>
            </div>

            <div className="flex items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-md animate-fade-up">
                    <Link to="/" className="lg:hidden font-display text-2xl font-extrabold tracking-tight block mb-8">{BRAND_NAME}</Link>

                    <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-3">Sign in</div>
                    <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                        Welcome back, counsel.
                    </h1>
                    <p className="text-slate-600 mt-2 text-sm">Sign in to your firm workspace.</p>

                    {GOOGLE_CLIENT_ID ? (
                        <div className="mt-8" data-testid="google-signin-wrap">
                            <GoogleLogin
                                onSuccess={onGoogleSuccess}
                                onError={() => toast.error("Google sign-in failed")}
                                width="384"
                                useOneTap={false}
                            />
                        </div>
                    ) : (
                        <div className="mt-8 text-[11px] font-mono text-slate-400 border border-dashed border-slate-200 px-3 py-2 rounded-sm">
                            Google sign-in not configured (set REACT_APP_GOOGLE_CLIENT_ID).
                        </div>
                    )}

                    <div className="my-5 flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400">
                        <div className="flex-1 h-px bg-slate-200" />
                        or with email
                        <div className="flex-1 h-px bg-slate-200" />
                    </div>

                    <form onSubmit={onSubmit} className="space-y-4">
                        <div>
                            <label className="label-sharp">Email</label>
                            <input
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                type="email"
                                required
                                className="input-sharp"
                                data-testid="login-email-input"
                                placeholder="you@lawfirm.com"
                            />
                        </div>
                        <div>
                            <label className="label-sharp">Password</label>
                            <input
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                type="password"
                                required
                                className="input-sharp"
                                data-testid="login-password-input"
                            />
                        </div>
                        {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm" data-testid="login-error">{err}</div>}
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="login-submit-button"
                            className="btn-primary w-full disabled:opacity-60"
                        >
                            {loading ? "Signing in…" : "Sign in"}
                        </button>
                    </form>

                    <div className="text-xs text-slate-500 text-center mt-6">
                        No account? <Link to="/register" className="text-[#0B2447] font-semibold hover:underline" data-testid="register-link">Start trial</Link>
                    </div>
                    <div className="mt-8 p-3 bg-slate-50 border border-slate-200 rounded-sm text-[11px] font-mono text-slate-500">
                        DEMO ACCOUNT: demo@lawfirm.com · Demo@2026
                    </div>
                </div>
            </div>
        </div>
    );

    if (!GOOGLE_CLIENT_ID) return inner;
    return <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{inner}</GoogleOAuthProvider>;
}
